import { Button, PageContent, PageHeader } from '@/components';
import { useMinistries } from '@/features/master-table/hooks';
import { getMinistryName } from '@/features/mold/utils';
import { useProject } from '@/features/project/_hooks/project';
import { useChangeBrowserTabTitleEffect, useId } from '@/hooks';
import { isPS } from '@/utils';
import { type FC, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import { ProjectsPageDynamicLink } from '../../../_components/ProjectsPageDynamicLink';
import { IndonesiaProjectLedgerPage } from '../_components/IndonesiaProjectLedgerPage/IndonesiaProjectLedgerPage';
import { KsProjectLedgerPage } from '../_components/KsProjectLedgerPage';
import { RsProjectLedgerPage } from '../_components/RsProjectLedgerPage';
import { SsProjectLedgerPage } from '../_components/SsProjectLedgerPage';

type Props = {
  onClickPrintButton?: () => void;
};

export const ProjectLedgerElement: FC<Props> = ({
  onClickPrintButton = undefined,
}) => {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);

  const { id } = useId();
  const project = useProject(id);
  const ministries = useMinistries();

  const ministryName = getMinistryName({
    targetId: project.data?.ministryId,
    ministries: ministries.data,
  });

  const title = useMemo(
    () =>
      project.data &&
      ministryName &&
      `${project.data.fiscalYear}-${ministryName}-${project.data.name}`,
    [ministryName, project.data],
  );

  useChangeBrowserTabTitleEffect({
    title,
    hideFallback: true,
  });

  const handlePrint = useReactToPrint({
    contentRef,
    pageStyle: '@page { size: A4 landscape; margin: 10mm 5mm; }',
    documentTitle: title,
  });

  const handlePrintPdf = useCallback(() => {
    if (onClickPrintButton) {
      onClickPrintButton();
      return;
    }
    handlePrint();
  }, [onClickPrintButton, handlePrint]);

  return (
    <>
      <PageHeader
        title={
          isPS() ? t('preview-print-page') : t('preview-report-projects-page')
        }
        breadcrumbs={[
          { title: <ProjectsPageDynamicLink /> },
          {
            title: t('project-details-page'),
            href: isPS()
              ? `${location.pathname.replace('/public-site', '').split('/').slice(0, -1).join('/')}`
              : `${location.pathname.split('/').slice(0, -1).join('/')}`,
          },
          {
            title: isPS()
              ? t('preview-print-page')
              : t('preview-report-projects-page'),
          },
        ]}
        buttons={
          <Button type="primary" onClick={handlePrintPdf}>
            {t('export-pdf')}
          </Button>
        }
      />
      <PageContent>
        <div ref={contentRef}>
          {project.data?.indonesiaData ? (
            <IndonesiaProjectLedgerPage />
          ) : (
            <>
              {project.data?.sheetType === 'RS' && <RsProjectLedgerPage />}
              {project.data?.sheetType === 'SS' && <SsProjectLedgerPage />}
              {project.data?.sheetType === 'KS' && <KsProjectLedgerPage />}
            </>
          )}
        </div>
      </PageContent>
    </>
  );
};
